// @fluxora/auth — dummy (dev-only, in-memory) auth provider.
//
// DEV ONLY. Guarded at the call site by the consuming app's `devMode` env
// flag + a bundle-grep CI gate. Holds the current session in React state,
// matches logins against the fixture `users` prop, and feeds BOTH the
// dummy-specific context (consumed by the US authStore selector hooks) AND
// the shared AuthContext (consumed by `useAuth`).
//
// No real credentials, no network, no localStorage of secrets. Any password
// is accepted for a known fixture email.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import type { UserRole } from '@fluxora/types';

import { AuthContext } from './context.js';
import type {
  AuthContextValue,
  AuthUser,
  DummyAuthContextValue,
  DummyAuthState,
  DummyOrganization,
  DummyUser,
  DummyUserRole,
} from './types.js';

// Dummy-specific context. Separate from the shared AuthContext because the US
// authStore reads the raw `DummyAuthState` (token / tenantId / user.role)
// which is richer than the generic AuthUser projection.
const DummyAuthContext = createContext<DummyAuthContextValue | null>(null);

const DEFAULT_DEV_COUNTRY = 'US';
const DEV_SESSION_TTL_SECONDS = 3600;

/** A stable, well-formed-looking dev token. NOT a real JWT — dev only. */
function makeDevToken(user: DummyUser, tenantId: string): string {
  // Three dot-separated base64url-ish segments so anything that splits on '.'
  // (header.payload.signature) does not choke. Payload is decorative.
  const header = 'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0';
  const payload = btoa(
    JSON.stringify({ sub: user.id, tenant_id: tenantId, role: user.role }),
  )
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `${header}.${payload}.dev`;
}

/** Map a dummy fixture role onto the coarse cross-cutting UserRole grammar. */
function toUserRole(role: DummyUserRole): UserRole {
  switch (role) {
    case 'admin':
      return 'admin';
    case 'viewer':
      return 'viewer';
    // `sales` / `ops` have no exact UserRole equivalent; treat as `user`.
    case 'sales':
    case 'ops':
      return 'user';
  }
}

/** Project a DummyAuthState onto the provider-agnostic AuthUser. */
function toAuthUser(state: DummyAuthState): AuthUser {
  return {
    id: state.user.id,
    email: state.user.email,
    name: state.user.displayName,
    roles: [toUserRole(state.user.role)],
    tenantId: state.tenantId,
    countryCode: state.countryCode,
    scopes: [`tenant:${state.tenantId}:${state.user.role}`],
    photoUrl: state.user.photoUrl,
  };
}

export interface DummyAuthProviderProps {
  users: readonly DummyUser[];
  organization: DummyOrganization;
  children: ReactNode;
}

// --- Test seam --------------------------------------------------------------
//
// RequireAuth.test.tsx imports `__dummyAuthTestHooks` and calls
// `.seed({ user, organization, tenantId, token, expiresAt })` to pre-populate
// an authenticated session before render. We back this with a module-scope
// pending-seed slot that the next mounted provider picks up as its initial
// state. `.reset()` clears it.

let pendingSeed: DummyAuthState | null = null;

export interface DummyAuthSeed {
  user: DummyUser;
  organization: DummyOrganization;
  tenantId: string;
  token: string;
  expiresAt: number;
  countryCode?: string;
}

export const __dummyAuthTestHooks = {
  /** Seed an authenticated session for the NEXT mounted DummyAuthProvider. */
  seed(seed: DummyAuthSeed): void {
    pendingSeed = {
      user: seed.user,
      organization: seed.organization,
      tenantId: seed.tenantId,
      token: seed.token,
      countryCode: seed.countryCode ?? DEFAULT_DEV_COUNTRY,
      expiresAt: seed.expiresAt,
    };
  },
  /** Clear any pending seed. */
  reset(): void {
    pendingSeed = null;
  },
};

export function DummyAuthProvider({
  users,
  organization,
  children,
}: DummyAuthProviderProps): ReactElement {
  // Pick up a test seed exactly once, on first render.
  const [state, setState] = useState<DummyAuthState | null>(() => {
    if (pendingSeed) {
      const seeded = pendingSeed;
      pendingSeed = null;
      return seeded;
    }
    return null;
  });
  const [isHydrated, setHydrated] = useState(false);

  // Mark hydrated after first commit (mirrors the real provider's async
  // session restore, but resolves synchronously for dev).
  useEffect(() => {
    setHydrated(true);
  }, []);

  const login = useCallback(
    async (email: string, _password: string): Promise<void> => {
      const match = users.find(
        (u) => u.email.toLowerCase() === email.trim().toLowerCase(),
      );
      if (!match) {
        // LoginPage surfaces this message in its aria-live region; the US
        // LoginPage test asserts /Unknown user/.
        throw new Error(`Unknown user: ${email}`);
      }
      const tenantId = organization.id;
      setState({
        user: match,
        organization,
        token: makeDevToken(match, tenantId),
        tenantId,
        countryCode: organization.countryCode ?? DEFAULT_DEV_COUNTRY,
        expiresAt: Math.floor(Date.now() / 1000) + DEV_SESSION_TTL_SECONDS,
      });
    },
    [users, organization],
  );

  const logout = useCallback((): void => {
    setState(null);
  }, []);

  const dummyValue = useMemo<DummyAuthContextValue>(
    () => ({
      state,
      isAuthenticated: state?.token != null,
      isHydrated,
      isLoading: false,
      login,
      logout,
    }),
    [state, isHydrated, login, logout],
  );

  // Feed the shared AuthContext too, so provider-agnostic `useAuth` works.
  const sharedValue = useMemo<AuthContextValue>(() => {
    const user = state ? toAuthUser(state) : null;
    return {
      state: user,
      user,
      isAuthenticated: state?.token != null,
      isHydrated,
      isLoading: false,
      login,
      logout: async () => logout(),
      token: state?.token ?? null,
    };
  }, [state, isHydrated, login, logout]);

  return (
    <AuthContext.Provider value={sharedValue}>
      <DummyAuthContext.Provider value={dummyValue}>{children}</DummyAuthContext.Provider>
    </AuthContext.Provider>
  );
}

// --- Hooks ------------------------------------------------------------------

export function useDummyAuth(): DummyAuthContextValue {
  const ctx = useContext(DummyAuthContext);
  if (!ctx) {
    throw new Error('useDummyAuth must be used within a <DummyAuthProvider>');
  }
  return ctx;
}

/** Access token, or null. */
export function useAuthToken(): string | null {
  return useDummyAuth().state?.token ?? null;
}

/** Active tenant id, or null (US authStore wraps this with an env fallback). */
export function useTenantId(): string | null {
  return useDummyAuth().state?.tenantId ?? null;
}

/** Actor user id (`sub`), or null. */
export function useActorUserId(): string | null {
  return useDummyAuth().state?.user.id ?? null;
}

/** The full fixture user, or null. */
export function useCurrentUser(): DummyUser | null {
  return useDummyAuth().state?.user ?? null;
}

/** The fixture role, or null. */
export function useUserRole(): DummyUserRole | null {
  return useDummyAuth().state?.user.role ?? null;
}
